import { inject, Injectable } from '@angular/core';
import {
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  GetCommunityFirstUnreadNoticeIdGQL,
  GetMyCommunitiesFeedGQL,
  GetMyCommunitiesGQL,
  IsCommunityAdminGQL,
  IsMemberOfCommunityGQL,
  WasMemberOfArchivedCommunityGQL,
} from '../graphql/generated/services';
import type {
  InviteCommunityPreview,
  MyCommunityFeedItem,
  MyCommunitySummary,
} from '../models/community.model';
import { normalizeCacheBustTimestamp } from '../utils/normalize-cache-bust-timestamp';
import { AuthService } from './auth.service';

const STORAGE_KEY_CURRENT_COMMUNITY_ID = 'commo_current_community_id';
const STORAGE_KEY_CURRENT_COMMUNITY_USER_ID = 'commo_current_community_user_id';

/** GraphQL／フィード RPC 共通の所属コミュニティ列。 */
type MyCommunitySummarySource = {
  id: string;
  name?: string | null;
  allow_guest_joins?: boolean | null;
  calendar_enabled?: boolean | null;
  role?: string | null;
  icon_path?: string | null;
  icon_updated_at?: string | null;
};

@Injectable({ providedIn: 'root' })
export class CommunityService {
  private readonly authService = inject(AuthService);
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly getMyCommunitiesGql = inject(GetMyCommunitiesGQL);
  private readonly getMyCommunitiesFeedGql = inject(GetMyCommunitiesFeedGQL);
  private readonly getCommunityFirstUnreadNoticeIdGql = inject(
    GetCommunityFirstUnreadNoticeIdGQL,
  );
  private readonly isCommunityAdminGql = inject(IsCommunityAdminGQL);
  private readonly isMemberOfCommunityGql = inject(IsMemberOfCommunityGQL);
  private readonly wasMemberOfArchivedCommunityGql = inject(
    WasMemberOfArchivedCommunityGQL,
  );
  private currentCommunityIdFetchInFlight$: Observable<string | null> | null =
    null;
  /** 同時呼び出しを1本にまとめる（一覧の admin 解決と切替アイコン列の取得が競合しうる）。 */
  private myCommunitiesInFlight$: Observable<MyCommunitySummary[]> | null =
    null;
  private myCommunitiesFeedInFlight$: Observable<MyCommunityFeedItem[]> | null =
    null;

  /** 指定コミュニティにおいて現在のユーザーが管理者かどうかを取得する。 */
  fetchIsCommunityAdmin(communityId: string): Observable<boolean> {
    return this.isCommunityAdminGql
      .fetch({
        variables: { community_id: communityId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.error) throw new Error(result.error.message);
          return Boolean(result.data?.is_community_admin);
        }),
      );
  }

  /** localStorage に保存された現在のコミュニティ ID を返す。現在ユーザーと一致するときのみ有効。 */
  getCurrentCommunityId(): string | null {
    if (typeof localStorage === 'undefined') return null;
    const currentUserId = this.authService.user()?.id ?? null;
    const storedUserId = localStorage.getItem(
      STORAGE_KEY_CURRENT_COMMUNITY_USER_ID,
    );
    if (currentUserId !== storedUserId) return null;
    return localStorage.getItem(STORAGE_KEY_CURRENT_COMMUNITY_ID);
  }

  /** 現在のコミュニティ ID を保存する（スイッチャー用）。現在ユーザーも一緒に保存する。 */
  setCurrentCommunityId(communityId: string): void {
    const userId = this.authService.user()?.id ?? '';
    localStorage.setItem(STORAGE_KEY_CURRENT_COMMUNITY_ID, communityId);
    localStorage.setItem(STORAGE_KEY_CURRENT_COMMUNITY_USER_ID, userId);
  }

  /** 現在ユーザーに紐づくコミュニティ選択キャッシュを削除する。 */
  private clearCurrentCommunityId(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY_CURRENT_COMMUNITY_ID);
    localStorage.removeItem(STORAGE_KEY_CURRENT_COMMUNITY_USER_ID);
  }

  /** GraphQL／フィード RPC 共通の MyCommunitySummary 組み立て。 */
  private toMyCommunitySummary(
    row: MyCommunitySummarySource,
  ): MyCommunitySummary {
    const roleRaw = (row.role ?? '').trim().toLowerCase();
    return {
      id: row.id,
      name: row.name?.trim() || 'Untitled',
      allowGuestJoins: row.allow_guest_joins !== false,
      calendarEnabled: row.calendar_enabled === true,
      role: roleRaw === 'admin' ? 'admin' : 'member',
      iconPath: row.icon_path?.trim() || null,
      iconUpdatedAt: normalizeCacheBustTimestamp(row.icon_updated_at),
    };
  }

  /** コミュニティを新規作成し、作成者を管理者として登録する。戻り値は新規コミュニティの ID。 */
  createCommunity(
    name: string,
    allowGuestJoins = true,
    calendarEnabled = false,
  ): Observable<string> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Please enter a community name.');
    }
    return from(
      this.supabase.rpc('create_community', {
        p_name: trimmed,
        p_allow_guest_joins: allowGuestJoins,
        p_calendar_enabled: calendarEnabled,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const id = data as string | null;
        if (!id) throw new Error('Failed to create community.');
        this.setCurrentCommunityId(id);
        return id;
      }),
    );
  }

  /**
   * コミュニティ招待コードで参加する。公開お知らせは不要。
   * @returns 参加先コミュニティ ID
   */
  ensureMemberByInviteCode(code: string): Observable<string> {
    return from(
      this.supabase.rpc('ensure_member_by_invite_code', {
        p_code: code,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const id = data as string | null;
        if (!id) throw new Error('Invite not found.');
        return id;
      }),
    );
  }

  /** 招待コードのコミュニティに現在ユーザーが所属しているか。 */
  isMemberByInviteCode(code: string): Observable<boolean> {
    return from(
      this.supabase.rpc('is_member_by_invite_code', {
        p_code: code,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        return Boolean(data);
      }),
    );
  }

  /**
   * コミュニティ招待のプレビュー。無効・アーカイブは行なし → Invite not found。
   */
  getInviteCommunityPreview(code: string): Observable<InviteCommunityPreview> {
    return from(
      this.supabase.rpc('get_invite_community_preview', {
        p_code: code,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const row = (
          data as Array<{
            community_id: string | null;
            community_name: string | null;
            allow_guest_joins: boolean | null;
          }> | null
        )?.[0];
        if (!row?.community_id) {
          throw new Error('Invite not found.');
        }
        return {
          communityId: row.community_id,
          communityName: row.community_name?.trim() || 'Unknown community',
          allowGuestJoins: row.allow_guest_joins !== false,
        };
      }),
    );
  }

  /** 管理者向け: 現行の招待コードを取得する。 */
  getCommunityInviteCode(communityId: string): Observable<string> {
    return from(
      this.supabase.rpc('get_community_invite_code', {
        p_community_id: communityId,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const code = (data as string | null)?.trim() ?? '';
        if (!code) throw new Error('Invite not found.');
        return code;
      }),
    );
  }

  /**
   * 管理者向け: 招待コードを回転する（旧コード即無効）。
   * @returns 新しいコード
   */
  rotateCommunityInviteCode(communityId: string): Observable<string> {
    return from(
      this.supabase.rpc('rotate_community_invite_code', {
        p_community_id: communityId,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        const code = (data as string | null)?.trim() ?? '';
        if (!code) throw new Error('Failed to rotate invite code.');
        return code;
      }),
    );
  }

  /**
   * 現在ユーザーがそのコミュニティの唯一の管理者か（この場合は抜け不可 UI 用）。
   */
  fetchIsSoleCommunityAdmin(communityId: string): Observable<boolean> {
    return from(
      this.supabase.rpc('is_sole_community_admin', {
        p_community_id: communityId,
      }),
    ).pipe(
      map(({ data, error }) => {
        if (error) throw new Error(error.message);
        return Boolean(data);
      }),
    );
  }

  /** 現在のコミュニティから抜ける（最後の管理者のみのときは RPC が拒否する）。 */
  leaveCommunity(communityId: string): Observable<void> {
    return from(
      this.supabase.rpc('leave_community', {
        p_community_id: communityId,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
        this.clearStoredCurrentCommunityIfMatches(communityId);
      }),
    );
  }

  /** 保存中の「現在コミュニティ」が指定 ID と一致するときだけクリアする。 */
  clearStoredCurrentCommunityIfMatches(communityId: string): void {
    if (this.getCurrentCommunityId() === communityId) {
      this.clearCurrentCommunityId();
    }
  }

  /** 指定コミュニティに現在ユーザーが所属しているか。 */
  isMemberOfCommunity(communityId: string): Observable<boolean> {
    return this.isMemberOfCommunityGql
      .fetch({
        variables: { community_id: communityId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.error) throw new Error(result.error.message);
          return Boolean(result.data?.is_member_of_community);
        }),
      );
  }

  /** 所属コミュニティ一覧（最大 100 件）。role 付き GraphQL（1 クエリ）。 */
  fetchMyCommunities(): Observable<MyCommunitySummary[]> {
    const inFlight = this.myCommunitiesInFlight$;
    if (inFlight) {
      return inFlight;
    }
    const request$ = from(this.supabase.auth.getUser()).pipe(
      switchMap(({ data: { user } }) => {
        if (!user?.id) {
          return of([]);
        }
        return this.getMyCommunitiesGql
          .fetch({
            variables: { user_id: user.id },
            fetchPolicy: 'network-only',
          })
          .pipe(
            map((result) => {
              const edges =
                result.data?.community_membersCollection?.edges ?? [];
              const list: MyCommunitySummary[] = [];
              for (const edge of edges) {
                const node = edge?.node;
                const community = node?.communities;
                const id = community?.id ?? '';
                if (!id || community?.archived_at != null) continue;
                list.push(
                  this.toMyCommunitySummary({
                    id,
                    name: community?.name,
                    allow_guest_joins: community?.allow_guest_joins,
                    calendar_enabled: community?.calendar_enabled,
                    role: node?.role,
                    icon_path: community?.icon_path,
                    icon_updated_at: community?.icon_updated_at,
                  }),
                );
              }
              return list;
            }),
          );
      }),
      finalize(() => {
        this.myCommunitiesInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.myCommunitiesInFlight$ = request$;
    return request$;
  }

  /** コミュニティ一覧用（最終お知らせ・未読件数）。活動日時の新しい順。 */
  fetchMyCommunitiesFeed(): Observable<MyCommunityFeedItem[]> {
    const inFlight = this.myCommunitiesFeedInFlight$;
    if (inFlight) {
      return inFlight;
    }
    const request$ = from(this.supabase.auth.getUser()).pipe(
      switchMap(({ data: { user } }) => {
        if (!user?.id) {
          return of([]);
        }
        return this.loadMyCommunitiesFeed();
      }),
      finalize(() => {
        this.myCommunitiesFeedInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.myCommunitiesFeedInFlight$ = request$;
    return request$;
  }

  /** 壁で見えた公開お知らせの publish_at まで水位を進める（GREATEST）。 */
  advanceNoticeReadWatermark(
    communityId: string,
    publishAt: string,
  ): Observable<void> {
    return from(
      this.supabase.rpc('advance_community_notice_read_watermark', {
        p_community_id: communityId,
        p_publish_at: publishAt,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }

  /** いちばん古い未読お知らせ ID。無ければ null。 */
  fetchFirstUnreadNoticeId(communityId: string): Observable<string | null> {
    return this.getCommunityFirstUnreadNoticeIdGql
      .fetch({
        variables: { community_id: communityId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.error) throw new Error(result.error.message);
          const id = result.data?.get_community_first_unread_notice_id;
          return typeof id === 'string' && id.length > 0 ? id : null;
        }),
      );
  }

  /** pg_graphql は `first` を 30 で黙って切る。所属がそれを超えたらカーソルで足す。 */
  private loadMyCommunitiesFeed(): Observable<MyCommunityFeedItem[]> {
    const seenCursors = new Set<string>();
    const loadPage = (
      after: string | null,
    ): Observable<MyCommunityFeedItem[]> =>
      this.getMyCommunitiesFeedGql
        .fetch({
          variables: after ? { after } : {},
          fetchPolicy: 'network-only',
        })
        .pipe(
          switchMap((result) => {
            if (result.error) throw new Error(result.error.message);
            const connection = result.data?.my_communities_feedCollection;
            const page = (connection?.edges ?? [])
              .map((edge) => edge?.node)
              .filter(
                (node): node is NonNullable<typeof node> & { id: string } =>
                  typeof node?.id === 'string' && node.id.length > 0,
              )
              .map((node) => {
                const unreadRaw = node.unread_count;
                const unreadCount =
                  typeof unreadRaw === 'number'
                    ? unreadRaw
                    : Number(unreadRaw ?? 0) || 0;
                const publishAt = node.last_notice_publish_at;
                return {
                  ...this.toMyCommunitySummary(node),
                  lastNoticeTitle: node.last_notice_title?.trim() || null,
                  lastNoticePublishAt:
                    typeof publishAt === 'string' && publishAt.length > 0
                      ? publishAt
                      : null,
                  unreadCount: Math.max(0, unreadCount),
                };
              });
            const endCursor = connection?.pageInfo.endCursor ?? null;
            if (
              connection?.pageInfo.hasNextPage &&
              endCursor &&
              !seenCursors.has(endCursor)
            ) {
              seenCursors.add(endCursor);
              return loadPage(endCursor).pipe(
                map((rest) => [...page, ...rest]),
              );
            }
            return of(page);
          }),
        );
    return loadPage(null);
  }

  /**
   * 現在ユーザーが管理者のコミュニティだけ返す（Members タブ／コミュニティ切替用）。
   * `fetchMyCommunities` の role で絞る（`is_community_admin` の N 回 RPC はしない）。
   */
  fetchMyAdminCommunities(): Observable<MyCommunitySummary[]> {
    return this.fetchMyCommunities().pipe(
      map((list) => list.filter((c) => c.role === 'admin')),
    );
  }

  /**
   * ゲスト参加可否を更新する（管理者のみ）。
   */
  updateCommunityGuestJoins(
    communityId: string,
    allowGuestJoins: boolean,
  ): Observable<void> {
    return from(
      this.supabase.rpc('update_community_guest_joins', {
        p_community_id: communityId,
        p_allow_guest_joins: allowGuestJoins,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }

  /** カレンダー利用可否を更新する（管理者のみ）。オフでもイベント行は保持する。 */
  updateCommunityCalendarEnabled(
    communityId: string,
    calendarEnabled: boolean,
  ): Observable<void> {
    return from(
      this.supabase.rpc('update_community_calendar_enabled', {
        p_community_id: communityId,
        p_calendar_enabled: calendarEnabled,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }

  /** アーカイブ済みコミュニティに、終了前に所属していたメンバーか（終了案内ルート用）。 */
  wasMemberOfArchivedCommunity(communityId: string): Observable<boolean> {
    return this.wasMemberOfArchivedCommunityGql
      .fetch({
        variables: { community_id: communityId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.error) throw new Error(result.error.message);
          return Boolean(result.data?.was_member_of_archived_community);
        }),
      );
  }

  /**
   * コミュニティ名を変更する（管理者のみ）。空名・他コミュニティとの重複は RPC が拒否する。
   */
  renameCommunity(communityId: string, name: string): Observable<string> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Please enter a community name.');
    }
    return from(
      this.supabase.rpc('update_community_name', {
        p_community_id: communityId,
        p_name: trimmed,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
        return trimmed;
      }),
    );
  }

  /**
   * コミュニティをアーカイブする（管理者のみ）。`p_confirmation_name` は DB の name と trim 一致が必須。
   */
  archiveCommunity(
    communityId: string,
    confirmationName: string,
  ): Observable<void> {
    return from(
      this.supabase.rpc('archive_community', {
        p_community_id: communityId,
        p_confirmation_name: confirmationName.trim(),
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
        this.clearStoredCurrentCommunityIfMatches(communityId);
      }),
    );
  }

  /**
   * 現在のコミュニティ ID を返す。
   * localStorage の値が残っていても、現在の所属一覧に存在するか検証してから返す。
   * 存在しない場合は表示順先頭へフォールバックし、所属 0 件ならキャッシュを消して null を返す。
   */
  getCurrentCommunityIdOrFetchFirst(): Observable<string | null> {
    const inFlight = this.currentCommunityIdFetchInFlight$;
    if (inFlight) {
      return inFlight;
    }

    const stored = this.getCurrentCommunityId();
    const request$ = this.fetchMyCommunities().pipe(
      map((list) => {
        const ids = list.map((c) => c.id);
        if (stored && ids.includes(stored)) {
          return stored;
        }
        const firstId = ids[0] ?? null;
        if (firstId) {
          this.setCurrentCommunityId(firstId);
          return firstId;
        }
        this.clearCurrentCommunityId();
        return null;
      }),
      finalize(() => {
        this.currentCommunityIdFetchInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.currentCommunityIdFetchInFlight$ = request$;
    return request$;
  }
}
